<template>
  <div class="server-status">
    <v-container class="py-6">
      <!-- Header -->
      <v-row class="mb-6">
        <v-col cols="12">
          <div class="d-flex flex-column flex-sm-row justify-space-between align-start align-sm-center ga-3">
            <div>
              <h1 class="text-h4 text-sm-h3 text-gradient mb-2">
                <v-icon icon="mdi-server" class="me-2" />
                {{ $t('admin.serverStatusPage.title') }}
              </h1>
              <p class="text-body-1 text-sm-h6 text-medium-emphasis">
                {{ $t('admin.serverStatusPage.subtitle') }}
              </p>
            </div>
            <div>
              <v-btn
                color="primary"
                variant="outlined"
                :loading="loading"
                @click="refreshStatus"
                size="small"
                class="d-sm-none"
              >
                <v-icon start>mdi-refresh</v-icon>
                {{ $t('admin.serverStatusPage.refresh') }}
              </v-btn>
              <v-btn
                color="primary"
                variant="outlined"
                :loading="loading"
                @click="refreshStatus"
                class="d-none d-sm-flex"
              >
                <v-icon start>mdi-refresh</v-icon>
                {{ $t('admin.serverStatusPage.refresh') }}
              </v-btn>
            </div>
          </div>
        </v-col>
      </v-row>

      <!-- Loading State -->
      <v-row v-if="loading && !serverData">
        <v-col cols="12" class="text-center py-12">
          <v-progress-circular indeterminate color="primary" size="64" />
          <p class="text-medium-emphasis mt-4">{{ $t('admin.serverStatusPage.loading') }}</p>
        </v-col>
      </v-row>

      <template v-else-if="serverData">
        <!-- Health Status -->
        <v-row class="mb-6">
          <v-col cols="12">
            <v-card elevation="4" :color="healthStatus.healthy ? 'success' : 'error'" variant="tonal">
              <v-card-text class="d-flex align-center justify-space-between pa-6">
                <div class="d-flex align-center">
                  <v-icon 
                    :icon="healthStatus.healthy ? 'mdi-check-circle' : 'mdi-alert-circle'" 
                    size="56" 
                    class="me-4"
                  />
                  <div>
                    <h3 class="text-h5 font-weight-bold">
                      {{ healthStatus.healthy ? $t('admin.serverStatusPage.serverHealthy') : $t('admin.serverStatusPage.serverError') }}
                    </h3>
                    <p class="text-body-1 mb-0">
                      {{ $t('admin.serverStatusPage.lastCheck') }}: {{ formatTime(serverData.timestamp) }}
                    </p>
                  </div>
                </div>
                <div class="text-right">
                  <v-chip
                    v-for="(status, key) in healthStatus.checks"
                    :key="key"
                    :color="status ? 'success' : 'error'"
                    class="ma-1"
                    size="small"
                  >
                    <v-icon start size="small">{{ status ? 'mdi-check' : 'mdi-close' }}</v-icon>
                    {{ checkLabels[key] }}
                  </v-chip>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- System Stats Cards -->
        <v-row class="mb-6">
          <!-- CPU -->
          <v-col cols="12" sm="6" md="3">
            <v-card class="stat-card" elevation="4">
              <v-card-text class="text-center pa-6">
                <div class="icon-wrapper cpu mb-3">
                  <v-icon icon="mdi-chip" size="40" color="primary" />
                </div>
                <div class="text-h3 font-weight-bold mb-2" :class="getCpuColor(serverData.cpu.usage)">
                  {{ serverData.cpu.usage }}%
                </div>
                <div class="text-body-1 text-medium-emphasis">{{ $t('admin.serverStatusPage.cpuLoad') }}</div>
                <v-progress-linear
                  :model-value="parseFloat(serverData.cpu.usage)"
                  :color="getCpuProgressColor(serverData.cpu.usage)"
                  height="8"
                  class="mt-3"
                  rounded
                />
                <div class="text-caption text-medium-emphasis mt-2">
                  {{ serverData.cpu.cores }} {{ $t('admin.serverStatusPage.cores') }}
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- Memory -->
          <v-col cols="12" sm="6" md="3">
            <v-card class="stat-card" elevation="4">
              <v-card-text class="text-center pa-6">
                <div class="icon-wrapper memory mb-3">
                  <v-icon icon="mdi-memory" size="40" color="success" />
                </div>
                <div class="text-h3 font-weight-bold mb-2" :class="getMemoryColor(serverData.memory.percent)">
                  {{ serverData.memory.percent }}%
                </div>
                <div class="text-body-1 text-medium-emphasis">{{ $t('admin.serverStatusPage.memory') }}</div>
                <v-progress-linear
                  :model-value="parseFloat(serverData.memory.percent)"
                  :color="getMemoryProgressColor(serverData.memory.percent)"
                  height="8"
                  class="mt-3"
                  rounded
                />
                <div class="text-caption text-medium-emphasis mt-2">
                  {{ serverData.memory.used }}GB / {{ serverData.memory.total }}GB
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- Disk -->
          <v-col cols="12" sm="6" md="3">
            <v-card class="stat-card" elevation="4">
              <v-card-text class="text-center pa-6">
                <div class="icon-wrapper disk mb-3">
                  <v-icon icon="mdi-harddisk" size="40" color="warning" />
                </div>
                <div class="text-h3 font-weight-bold mb-2" :class="getDiskColor(serverData.disk.percent)">
                  {{ serverData.disk.percent }}%
                </div>
                <div class="text-body-1 text-medium-emphasis">{{ $t('admin.serverStatusPage.diskUsage') }}</div>
                <v-progress-linear
                  :model-value="parseFloat(serverData.disk.percent)"
                  :color="getDiskProgressColor(serverData.disk.percent)"
                  height="8"
                  class="mt-3"
                  rounded
                />
                <div class="text-caption text-medium-emphasis mt-2">
                  {{ serverData.disk.usedGB }}GB / {{ serverData.disk.totalGB }}GB
                </div>
                <div class="text-caption text-success mt-1">
                  <v-icon icon="mdi-check-circle" size="x-small" class="me-1" />
                  {{ $t('admin.serverStatusPage.free') }}: {{ serverData.disk.freeGB }}GB
                </div>
                <v-divider class="my-3" />
                <div class="d-flex justify-space-between text-caption">
                  <span>Public: {{ serverData.disk.publicGB }}GB</span>
                  <span>Uploads: {{ serverData.disk.uploadsGB }}GB</span>
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- Uptime -->
          <v-col cols="12" sm="6" md="3">
            <v-card class="stat-card" elevation="4">
              <v-card-text class="text-center pa-6">
                <div class="icon-wrapper uptime mb-3">
                  <v-icon icon="mdi-clock-outline" size="40" color="info" />
                </div>
                <div class="text-h4 font-weight-bold mb-2">
                  {{ serverData.uptime }}
                </div>
                <div class="text-body-1 text-medium-emphasis">{{ $t('admin.serverStatusPage.uptime') }}</div>
                <v-divider class="my-3" />
                <div class="text-caption text-medium-emphasis">
                  {{ $t('admin.serverStatusPage.activeUsers') }}: {{ serverData.activeUsers }}
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- System Info & Process Info -->
        <v-row class="mb-6">
          <!-- System Information -->
          <v-col cols="12" md="6">
            <v-card elevation="4">
              <v-card-title>
                <v-icon icon="mdi-information" class="me-2" color="primary" />
                {{ $t('admin.serverStatusPage.systemInfo') }}
              </v-card-title>
              <v-divider />
              <v-card-text>
                <v-list density="compact" class="bg-transparent">
                  <v-list-item>
                    <template #prepend>
                      <v-icon icon="mdi-desktop-tower" size="small" class="me-3" />
                    </template>
                    <v-list-item-title>{{ $t('admin.serverStatusPage.hostname') }}</v-list-item-title>
                    <template #append>
                      <span class="font-weight-bold">{{ serverData.system.hostname }}</span>
                    </template>
                  </v-list-item>
                  
                  <v-list-item>
                    <template #prepend>
                      <v-icon icon="mdi-linux" size="small" class="me-3" />
                    </template>
                    <v-list-item-title>{{ $t('admin.serverStatusPage.operatingSystem') }}</v-list-item-title>
                    <template #append>
                      <span class="font-weight-bold">{{ serverData.system.type }} {{ serverData.system.release }}</span>
                    </template>
                  </v-list-item>
                  
                  <v-list-item>
                    <template #prepend>
                      <v-icon icon="mdi-cog" size="small" class="me-3" />
                    </template>
                    <v-list-item-title>{{ $t('admin.serverStatusPage.platform') }}</v-list-item-title>
                    <template #append>
                      <span class="font-weight-bold">{{ serverData.system.platform }} ({{ serverData.system.arch }})</span>
                    </template>
                  </v-list-item>
                  
                  <v-list-item>
                    <template #prepend>
                      <v-icon icon="mdi-chip" size="small" class="me-3" />
                    </template>
                    <v-list-item-title>CPU</v-list-item-title>
                    <template #append>
                      <span class="font-weight-bold text-truncate" style="max-width: 250px;">
                        {{ serverData.cpu.model }}
                      </span>
                    </template>
                  </v-list-item>
                  
                  <v-list-item>
                    <template #prepend>
                      <v-icon icon="mdi-memory" size="small" class="me-3" />
                    </template>
                    <v-list-item-title>{{ $t('admin.serverStatusPage.totalMemory') }}</v-list-item-title>
                    <template #append>
                      <span class="font-weight-bold">{{ serverData.memory.total }} GB</span>
                    </template>
                  </v-list-item>
                </v-list>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- Process Info -->
          <v-col cols="12" md="6">
            <v-card elevation="4">
              <v-card-title>
                <v-icon icon="mdi-nodejs" class="me-2" color="success" />
                {{ $t('admin.serverStatusPage.nodeProcess') }}
              </v-card-title>
              <v-divider />
              <v-card-text>
                <v-list density="compact" class="bg-transparent">
                  <v-list-item>
                    <template #prepend>
                      <v-icon icon="mdi-identifier" size="small" class="me-3" />
                    </template>
                    <v-list-item-title>PID</v-list-item-title>
                    <template #append>
                      <v-chip color="primary" size="small">{{ serverData.process.pid }}</v-chip>
                    </template>
                  </v-list-item>
                  
                  <v-list-item>
                    <template #prepend>
                      <v-icon icon="mdi-nodejs" size="small" class="me-3" />
                    </template>
                    <v-list-item-title>{{ $t('admin.serverStatusPage.nodeVersion') }}</v-list-item-title>
                    <template #append>
                      <span class="font-weight-bold">{{ serverData.process.nodeVersion }}</span>
                    </template>
                  </v-list-item>
                  
                  <v-list-item>
                    <template #prepend>
                      <v-icon icon="mdi-memory" size="small" class="me-3" />
                    </template>
                    <v-list-item-title>{{ $t('admin.serverStatusPage.processMemory') }}</v-list-item-title>
                    <template #append>
                      <span class="font-weight-bold">{{ serverData.process.memoryMB }} MB</span>
                    </template>
                  </v-list-item>
                  
                  <v-list-item>
                    <template #prepend>
                      <v-icon icon="mdi-clock-start" size="small" class="me-3" />
                    </template>
                    <v-list-item-title>{{ $t('admin.serverStatusPage.processUptime') }}</v-list-item-title>
                    <template #append>
                      <span class="font-weight-bold">{{ serverData.process.uptimeMinutes }} {{ $t('admin.serverStatusPage.minutes') }}</span>
                    </template>
                  </v-list-item>
                  
                  <v-list-item>
                    <template #prepend>
                      <v-icon icon="mdi-database" size="small" class="me-3" />
                    </template>
                    <v-list-item-title>{{ $t('admin.serverStatusPage.database') }}</v-list-item-title>
                    <template #append>
                      <v-chip 
                        :color="serverData.database.status === 'connected' ? 'success' : 'error'" 
                        size="small"
                      >
                        <v-icon start size="small">
                          {{ serverData.database.status === 'connected' ? 'mdi-check' : 'mdi-close' }}
                        </v-icon>
                        {{ serverData.database.status === 'connected' ? $t('admin.serverStatusPage.dbConnected') : $t('admin.serverStatusPage.dbError') }}
                      </v-chip>
                    </template>
                  </v-list-item>
                </v-list>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Network Info -->
        <v-row v-if="serverData.network && serverData.network.length > 0">
          <v-col cols="12">
            <v-card elevation="4">
              <v-card-title>
                <v-icon icon="mdi-ethernet" class="me-2" color="info" />
                {{ $t('admin.serverStatusPage.networkInterfaces') }}
              </v-card-title>
              <v-divider />
              <v-card-text>
                <v-table density="comfortable">
                  <thead>
                    <tr>
                      <th>{{ $t('admin.serverStatusPage.interface') }}</th>
                      <th>{{ $t('admin.serverStatusPage.ipAddress') }}</th>
                      <th>{{ $t('admin.serverStatusPage.mask') }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="net in serverData.network" :key="net.name">
                      <td>
                        <v-chip size="small" color="primary" variant="outlined">
                          {{ net.name }}
                        </v-chip>
                      </td>
                      <td>
                        <code>{{ net.address }}</code>
                      </td>
                      <td>
                        <code>{{ net.netmask }}</code>
                      </td>
                    </tr>
                  </tbody>
                </v-table>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </template>

      <!-- Error State -->
      <v-row v-else-if="error">
        <v-col cols="12">
          <v-alert type="error" variant="tonal">
            <v-alert-title>{{ $t('admin.serverStatusPage.errorOccurred') }}</v-alert-title>
            {{ error }}
          </v-alert>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import api from '@/utils/api'

const { t } = useI18n()

// Refs
const loading = ref(false)
const error = ref(null)
const serverData = ref(null)
const healthStatus = ref({
  healthy: true,
  checks: {
    database: true,
    fileSystem: true,
    memory: true
  }
})

// Auto-refresh interval
let refreshInterval = null

// Labels for health checks - computed for reactivity with i18n
const checkLabels = computed(() => ({
  database: t('admin.serverStatusPage.checkDatabase'),
  fileSystem: t('admin.serverStatusPage.checkFileSystem'),
  memory: t('admin.serverStatusPage.checkMemory')
}))

// Methods
const refreshStatus = async () => {
  loading.value = true
  error.value = null
  
  try {
    const [statusResponse, healthResponse] = await Promise.all([
      api.get('/admin/server-status'),
      api.get('/admin/server-status/health')
    ])
    
    if (statusResponse.data.success) {
      serverData.value = statusResponse.data.data
    }
    
    if (healthResponse.data.success) {
      healthStatus.value = {
        healthy: healthResponse.data.healthy,
        checks: healthResponse.data.checks
      }
    }
  } catch (err) {
    console.error('Server status error:', err)
    error.value = err.response?.data?.error || t('admin.serverStatusPage.fetchError')
  } finally {
    loading.value = false
  }
}

const formatTime = (timestamp) => {
  if (!timestamp) return '-'
  const date = new Date(timestamp)
  return date.toLocaleString('kk-KZ', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  })
}

// Color helpers
const getCpuColor = (usage) => {
  const val = parseFloat(usage)
  if (val >= 90) return 'text-error'
  if (val >= 70) return 'text-warning'
  return 'text-success'
}

const getCpuProgressColor = (usage) => {
  const val = parseFloat(usage)
  if (val >= 90) return 'error'
  if (val >= 70) return 'warning'
  return 'success'
}

const getMemoryColor = (percent) => {
  const val = parseFloat(percent)
  if (val >= 90) return 'text-error'
  if (val >= 70) return 'text-warning'
  return 'text-success'
}

const getMemoryProgressColor = (percent) => {
  const val = parseFloat(percent)
  if (val >= 90) return 'error'
  if (val >= 70) return 'warning'
  return 'success'
}

const getDiskColor = (percent) => {
  const val = parseFloat(percent)
  if (val >= 90) return 'text-error'
  if (val >= 80) return 'text-warning'
  return 'text-success'
}

const getDiskProgressColor = (percent) => {
  const val = parseFloat(percent)
  if (val >= 90) return 'error'
  if (val >= 80) return 'warning'
  return 'success'
}

// Lifecycle
onMounted(() => {
  refreshStatus()
  // Auto-refresh every 30 seconds
  refreshInterval = setInterval(refreshStatus, 30000)
})

onUnmounted(() => {
  if (refreshInterval) {
    clearInterval(refreshInterval)
  }
})
</script>

<style scoped>
.server-status {
  min-height: 100vh;
  background-color: inherit;
}

/* Gradient text - works on all devices */
.text-gradient {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  -webkit-background-clip: text !important;
  -webkit-text-fill-color: transparent !important;
  background-clip: text !important;
  color: transparent !important;
}

/* Stat cards */
.stat-card {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  background-color: rgb(var(--v-theme-surface)) !important;
}

.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15) !important;
}

/* Icon wrappers with fixed colors */
.icon-wrapper {
  width: 70px;
  height: 70px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
}

.icon-wrapper.cpu {
  background: rgba(103, 126, 234, 0.15) !important;
}

.icon-wrapper.memory {
  background: rgba(76, 175, 80, 0.15) !important;
}

.icon-wrapper.disk {
  background: rgba(255, 152, 0, 0.15) !important;
}

.icon-wrapper.uptime {
  background: rgba(33, 150, 243, 0.15) !important;
}

/* Code blocks */
code {
  background: rgba(0, 0, 0, 0.1) !important;
  padding: 2px 8px;
  border-radius: 4px;
  font-family: 'Consolas', 'Monaco', monospace;
}

/* Force text colors */
.text-success {
  color: #4caf50 !important;
}

.text-warning {
  color: #ff9800 !important;
}

.text-error {
  color: #f44336 !important;
}

/* Mobile responsive styles */
@media (max-width: 600px) {
  .server-status .text-h3 {
    font-size: 1.5rem !important;
  }
  
  .server-status .text-h4 {
    font-size: 1.25rem !important;
  }
  
  .server-status .text-h5 {
    font-size: 1.1rem !important;
  }
  
  .stat-card .text-h3 {
    font-size: 1.75rem !important;
  }
  
  .icon-wrapper {
    width: 50px;
    height: 50px;
  }
  
  .icon-wrapper .v-icon {
    font-size: 28px !important;
  }
  
  .stat-card:hover {
    transform: none;
  }
  
  /* Health status card mobile */
  .v-card-text.d-flex.align-center.justify-space-between {
    flex-direction: column !important;
    text-align: center;
  }
  
  .v-card-text .text-right {
    text-align: center !important;
    margin-top: 12px;
  }
  
  .v-card-text .me-4 {
    margin-right: 0 !important;
    margin-bottom: 8px;
  }
}

/* Tablet styles */
@media (max-width: 960px) and (min-width: 601px) {
  .server-status .text-h3 {
    font-size: 1.75rem !important;
  }
  
  .icon-wrapper {
    width: 60px;
    height: 60px;
  }
}
</style>
